import { CSSProperties, useState } from 'react';

import {
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DndContext,
  DragOverlay,
} from '@dnd-kit/core';
import type { DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import cx from 'classnames';
import { move } from 'ramda';
import { components } from 'react-select-5';
import type { GroupBase, ValueContainerProps } from 'react-select-5';

import s from './index.module.css';
import { strategy } from './utils';

export function ValueContainer<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: ValueContainerProps<Option, IsMulti, Group>) {
  const { innerProps, children, getClassNames, getStyles, selectProps } = props;

  const getOptionValue = (option?: Option) =>
    // @ts-expect-error // React Select v5 compares options by 'value' prop by default if getOptionValue is not specified
    (selectProps.getOptionValue ? selectProps.getOptionValue(option || {}) : option?.value) || null;

  const getOptionLabel = (option?: Option) =>
    // React Select v5 compares options by 'label' prop by default if getOptionLabel is not specified
    selectProps.formatOptionLabel
      ? // @ts-expect-error
        selectProps.formatOptionLabel(option || {}, { context: 'value', ...selectProps })
      : // @ts-expect-error
        (selectProps.getOptionLabel ? selectProps.getOptionLabel(option || {}) : option?.label) || '';

  const value = props.getValue();
  const items = value.map((option) => ({
    ...option,
    id: getOptionValue(option),
  }));

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!(active && over) || !props.isMulti) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over?.id);
      (props as ValueContainerProps<Option, true, Group>).setValue(move(oldIndex, newIndex, value), 'select-option');
      setActiveId(null);
    }
  }

  if (!props.isMulti) {
    return (
      <div
        {...innerProps}
        className={getClassNames('valueContainer', props)}
        style={getStyles('valueContainer', props) as CSSProperties}
      >
        {children}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={strategy}>
        <div
          {...innerProps}
          className={getClassNames('valueContainer', props)}
          style={getStyles('valueContainer', props) as CSSProperties}
        >
          {children}
        </div>
      </SortableContext>
      <DragOverlay>
        <components.MultiValueContainer
          data={{}}
          innerProps={{
            // @ts-expect-error
            className: cx(getClassNames('multiValue', props), s.dragging),
            // @ts-expect-error
            style: { opacity: 1 },
          }}
          selectProps={props.selectProps}
        >
          <components.MultiValueLabel
            data={{}}
            // @ts-expect-error
            innerProps={{ className: getClassNames('multiValueLabel', props) }}
            selectProps={props.selectProps}
          >
            {getOptionLabel(value.find((v) => getOptionValue(v) === activeId))}
          </components.MultiValueLabel>
        </components.MultiValueContainer>
      </DragOverlay>
    </DndContext>
  );
}
